import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, useUser } from "@auth";
import { useGetLifestyleCategories, useGetLifestyleGroup, useGetPartnerPaymentMethods, useGetPartnerStatistics } from "@queries";
import { todos } from "./card-data";
import { HomePageTemplateProps } from "./template";
import { useCurrencyUtils } from "../../../utils"; // todo: implement @ pathing for utils
import { DashboardMetric, LifestyleSummaryData } from "../../../types"; // todo: implement @ pathing
import { useApproveLifestyleTransaction, useRejectLifestyleTransaction } from "@mutations";
import { createApproveTransactionHandler, createRejectTransactionHandler } from "../lifestyle/transaction-actions";
import { toast } from "sonner";

export const useHomePage = (): HomePageTemplateProps => {
  const { setSelectedPageIndex, partnerStatistics, isSetupComplete, calculateIsSetupComplete, partnerDashboardData, lifestyleCategories, billingDetails, setPartnerDashboardData, creditCardStatus } = useContext(AuthContext);
  const navigate = useNavigate();
  const partner = useUser();
  const approveLifestyleTransaction = useApproveLifestyleTransaction();
  const rejectLifestyleTransaction = useRejectLifestyleTransaction();
  const { getPartnerStatistics } = useGetPartnerStatistics();
  const { convertToCurrencyFormat } = useCurrencyUtils();
  const { getLifestyleCategories, isLoading: isLifestyleCategoriesLoading } = useGetLifestyleCategories();
  const { getLifestyleGroup } = useGetLifestyleGroup();
  const { getPartnerPaymentMethods, creditCards } = useGetPartnerPaymentMethods();
  const [approvingTransactionIds, setApprovingTransactionIds] = useState<Set<string>>(new Set());
  const [rejectingTransactionIds, setRejectingTransactionIds] = useState<Set<string>>(new Set());
  const [pendingTransactions, setPendingTransactions] = useState<LifestyleSummaryData["pendingTransactions"]>([]);
  
  // Loading states for different sections
  const hasLifestyleSectionLoadedRef = useRef(
    Boolean(partnerDashboardData?.lsaDashboardData) || !(partner?.lsaGroups?.length ?? 0)
  );
  const hasSavingsSectionLoadedRef = useRef(
    Boolean(partnerDashboardData?.dashboardData?.monthlySavings?.length)
  );
  const hasAirdropSectionLoadedRef = useRef(
    Boolean(partnerDashboardData?.dashboardData?.lastThreeAirdrops?.length)
  );

  const [isLoadingHeroMetrics, setIsLoadingHeroMetrics] = useState(true);
  const [isLoadingLifestyleData, setIsLoadingLifestyleData] = useState(!hasLifestyleSectionLoadedRef.current);
  const [isLoadingBrandData, setIsLoadingBrandData] = useState(true);
  const [isLoadingSavingsData, setIsLoadingSavingsData] = useState(!hasSavingsSectionLoadedRef.current);
  const [isLoadingAirdropData, setIsLoadingAirdropData] = useState(!hasAirdropSectionLoadedRef.current);
  
  useEffect(() => {
    calculateIsSetupComplete();
    getPartnerStatistics();
    getLifestyleCategories();
    getLifestyleGroup();
    
    // Simulate loading delays for different sections
    const heroTimer = setTimeout(() => setIsLoadingHeroMetrics(false), 800);
    const brandTimer = setTimeout(() => setIsLoadingBrandData(false), 1000);

    let savingsTimer: NodeJS.Timeout | undefined;
    if (!hasSavingsSectionLoadedRef.current) {
      savingsTimer = setTimeout(() => {
        setIsLoadingSavingsData(false);
        hasSavingsSectionLoadedRef.current = true;
      }, 1500);
    } else {
      setIsLoadingSavingsData(false);
    }

    let airdropTimer: NodeJS.Timeout | undefined;
    if (!hasAirdropSectionLoadedRef.current) {
      airdropTimer = setTimeout(() => {
        setIsLoadingAirdropData(false);
        hasAirdropSectionLoadedRef.current = true;
      }, 900);
    } else {
      setIsLoadingAirdropData(false);
    }
    
    return () => {
      clearTimeout(heroTimer);
      clearTimeout(brandTimer);
      if (savingsTimer) clearTimeout(savingsTimer);
      if (airdropTimer) clearTimeout(airdropTimer);
    };
  }, []);

  useEffect(() => {
    const hasLifestyleFund = (partner?.lsaGroups?.length ?? 0) > 0;

    if (!partner) {
      if (!hasLifestyleSectionLoadedRef.current) {
        setIsLoadingLifestyleData(true);
      }
      return;
    }

    if (!hasLifestyleFund) {
      setIsLoadingLifestyleData(false);
      hasLifestyleSectionLoadedRef.current = true;
      return;
    }

    const hasData = !isLifestyleCategoriesLoading && partnerDashboardData !== undefined;
    if (hasData) {
      setIsLoadingLifestyleData(false);
      hasLifestyleSectionLoadedRef.current = true;
    } else if (!hasLifestyleSectionLoadedRef.current) {
      setIsLoadingLifestyleData(true);
    }
  }, [partner, partnerDashboardData, isLifestyleCategoriesLoading]);

  useEffect(() => {
    console.log(JSON.stringify(partnerStatistics, null, 4));
  }, [partnerStatistics]);

  const resolvePaymentId = async (): Promise<string | null> => {
    const existing = billingDetails?.creditCards?.[0]?.paymentId ?? creditCards?.[0]?.paymentId;
    if (existing) {
      return existing;
    }
    try {
      const result = await getPartnerPaymentMethods();
      const fetched = (result as any)?.data?.getPartnerPaymentMethods?.[0]?.paymentId;
      return fetched ?? null;
    } catch (err) {
      console.error("Error fetching partner payment methods:", err);
      return null;
    }
  };

  const removePendingTransaction = (transactionId?: string) => {
    if (!transactionId) return;
    setPendingTransactions((prev) => prev.filter((tx) => tx.transactionId !== transactionId));
    if (setPartnerDashboardData && partnerDashboardData?.lsaDashboardData) {
      setPartnerDashboardData({
        ...partnerDashboardData,
        lsaDashboardData: {
          ...partnerDashboardData.lsaDashboardData,
          recentPendingTransactions:
            partnerDashboardData.lsaDashboardData.recentPendingTransactions?.filter(
              (txn) => txn.transactionId !== transactionId,
            ) || [],
        },
      });
    }
  };

  const approvePendingTransaction = createApproveTransactionHandler({
    resolvePaymentId,
    approveMutation: approveLifestyleTransaction,
    onStart: (transactionId) => {
      setApprovingTransactionIds((prev) => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
    },
    onSuccess: async (transactionId) => {
      removePendingTransaction(transactionId);
    },
    onError: (message) => toast.error(message),
    onFinally: (transactionId) => {
      setApprovingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    },
  });

  const rejectPendingTransaction = createRejectTransactionHandler({
    rejectMutation: rejectLifestyleTransaction,
    onStart: (transactionId) => {
      setRejectingTransactionIds((prev) => {
        const next = new Set(prev);
        next.add(transactionId);
        return next;
      });
    },
    onSuccess: async (transactionId) => {
      removePendingTransaction(transactionId);
    },
    onError: (message) => toast.error(message),
    onFinally: (transactionId) => {
      setRejectingTransactionIds((prev) => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    },
  });
 
  const navigateToPage = (index: number, to: string) => {
    // TODO: (Matt wonders...) what is the purpose if the index parameter? 
    setSelectedPageIndex(index);
    navigate(to);
  }

  useEffect(() => {
    console.log("Home page isSetupComplete: ", isSetupComplete);
  }, [isSetupComplete]);
  

  // Generate hero metrics data
  const getHeroMetrics = (): DashboardMetric[] => {
    const totalMembers = partner?.premiumSettings?.[0]?.users?.length || 0;
    const activeMembers = partner?.premiumSettings?.[0]?.users?.filter(user => !!user.userId).length || 0;
    const participationRate = totalMembers > 0 ? Math.round((activeMembers / totalMembers) * 100) : 0;
    const totalRewardsThisYear = partnerDashboardData?.dashboardData?.monthlySavings?.reduce((acc, curr) => acc + curr.savings, 0) || 0;

    return [
      {
        id: 'team-members',
        title: 'Team Members',
        value: totalMembers.toString(),
        icon: 'Users',
        color: 'bg-primary-lightest text-primary-medium'
      },
      {
        id: 'active-members',
        title: 'Onboarded Members',
        value: activeMembers.toString(),
        icon: 'UserCheck',
        color: 'bg-primary-lightest text-primary-medium'
      },
      {
        id: 'participation-rate',
        title: 'Onboarded Employee Rate',
        value: `${participationRate}%`,
        icon: 'TrendingUp',
        color: 'bg-primary-lightest text-primary-medium'
      },
      {
        id: 'total-rewards',
        title: 'Total Rewards All-Time',
        value: convertToCurrencyFormat(totalRewardsThisYear),
        icon: 'DollarSign',
        color: 'bg-primary-lightest text-primary-medium'
      }
    ];
  };

  // Generate lifestyle summary data
  useEffect(() => {
    console.log(">>>");
    console.log(partnerDashboardData?.lsaDashboardData?.recentPendingTransactions);
    
    const pendingRaw = partnerDashboardData?.lsaDashboardData?.recentPendingTransactions || [];
    const mappedPending =
      pendingRaw.map((transaction) => ({
        transactionId: transaction.transactionId,
        userId: transaction.userId,
        firstName: transaction.firstName,
        lastName: transaction.lastName,
        amount: transaction.amount ? transaction.amount / 100 : 0,
        date: transaction.date ? new Date(transaction.date) : new Date(),
        categoryId: transaction.categoryId,
        companyName: (transaction as any)?.brandName || (transaction as any)?.companyName || "", // <-------
        categoryName: lifestyleCategories?.find((category) => category.id === transaction.categoryId)?.name,
      })) || [];
    setPendingTransactions(mappedPending);
  }, [partnerDashboardData?.lsaDashboardData?.recentPendingTransactions, lifestyleCategories]);

  const getLifestyleSummary = (): LifestyleSummaryData => {
    const hasLifestyleFund = partner?.lsaGroups && partner.lsaGroups.length > 0;
    
    if (!hasLifestyleFund) {
      return {
        totalReimbursed: 0,
        participationRate: 0,
        avgPerUser: 0,
        pendingTransactions: [],
        hasLifestyleFund: false
      };
    }

    return {
      totalReimbursed: partnerDashboardData?.lsaDashboardData?.totalReimbursed ? partnerDashboardData?.lsaDashboardData?.totalReimbursed / 100 : 0,
      participationRate: partnerDashboardData?.lsaDashboardData?.participationRate || 0,
      avgPerUser: partnerDashboardData?.lsaDashboardData?.averageReimbursedPerUser ? partnerDashboardData?.lsaDashboardData?.averageReimbursedPerUser / 100 : 0,
      pendingTransactions,
      hasLifestyleFund: true
    };
  };

  return { 
    todos,
    partner,
    navigateToPage,
    isSetupComplete,
    billingDetails,
    partnerDashboardData,
    getHeroMetrics,
    getLifestyleSummary,
    onApprovePendingTransaction: approvePendingTransaction,
    onRejectPendingTransaction: rejectPendingTransaction,
    approvingTransactionIds,
    rejectingTransactionIds,
    isLoadingHeroMetrics,
    isLoadingLifestyleData,
    isLoadingBrandData,
    isLoadingSavingsData,
    isLoadingAirdropData,
    creditCardStatus
   };
};
